import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, ShieldCheck } from 'lucide-react';

const players = [
  {
    photo: 'https://customer-assets-4nw71qhi.emergentagent.net/job_elite-coaching-15/artifacts/965pl07h_Brooke%20results.webp',
    name: 'Brooke Barnes',
    team: 'Wauwatosa West • Girls Basketball',
    period: '2024–25 Season → 2025–26 Season',
    badge: null,
    mainStat: { label: '3PT%', before: '21.2%', after: '35.9%' },
    supporting: [
      { label: 'PPG', before: '4.0', after: '7.8' },
      { label: '3s Made', before: '25', after: '52' },
    ],
    caption: 'Scoring nearly doubled while her made threes more than doubled.',
  },
  {
    photo: 'https://customer-assets-4nw71qhi.emergentagent.net/job_elite-coaching-15/artifacts/ff7n31q0_jalen%20Brown%20shooting.webp',
    name: 'Jalen Brown',
    team: 'Wauwatosa West • Boys Basketball',
    period: '2024–25 Season → 2025–26 Season',
    badge: 'Wisconsin Commit',
    mainStat: { label: '3PT%', before: '27.4%', after: '37.6%' },
    supporting: [
      { label: '3s Made', before: '29', after: '47' },
      { label: 'FT%', before: '66.8%', after: '75.1%' },
    ],
    caption: 'Three-point shooting improved by more than 10 percentage points while he made 18 more threes than the previous season.',
  },
  {
    photo: 'https://customer-assets-4nw71qhi.emergentagent.net/job_elite-coaching-15/artifacts/ke5uu30f_Deuce.jpg',
    name: 'Deuce McDuffie',
    team: 'Nicolet HS → Nike EYBL',
    period: '2025–26 Nicolet HS Season → 2026 Nike EYBL',
    badge: 'HS → EYBL',
    mainStat: { label: '3PT%', before: '28.8%', after: '41.0%' },
    supporting: [],
    caption: "From his 2025–26 high school season to the 2026 Nike EYBL live period, Deuce's 3PT% jumped from 28.8% to 41.0% — a 12.2 percentage-point increase in the span of just a few months.",
  },
  {
    photo: 'https://customer-assets-4nw71qhi.emergentagent.net/job_elite-coaching-15/artifacts/w9zjaojt_Amelia.webp',
    name: 'Amelia Van Der Kooy',
    team: 'Oconomowoc • Girls Basketball',
    period: '2024–25 Season → 2025–26 Season',
    badge: null,
    mainStat: { label: '3s Made', before: '33', after: '49' },
    supporting: [
      { label: '3PT%', before: '27.3%', after: '32.2%' },
      { label: 'FT%', before: '72.9%', after: '78.9%' },
      { label: 'PPG', before: '10.8', after: '12.0' },
    ],
    caption: 'Amelia made 16 more threes, improved her 3PT% from 27.3% to 32.2% — nearly a 5 percentage-point jump — improved her free-throw percentage, and raised her scoring average.',
  },
];

export const PlayerResults = () => {
  return (
    <section id="player-results" data-testid="player-results" className="bg-neutral-950 py-14 sm:py-16 lg:py-20 px-6 border-y border-neutral-900">
      <div className="container mx-auto max-w-6xl">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.7 }}
          className="mb-12 text-center"
        >
          <h2 className="font-display text-white text-4xl sm:text-5xl uppercase">Player Development Results</h2>
          <p className="text-gray-400 mt-4">Real development. Real numbers. Verified by public stat sources.</p>
          <div className="w-16 h-1 bg-gray-400 mt-6 mx-auto" />
        </motion.div>

        <div className="grid md:grid-cols-2 gap-6">
          {players.map((p, i) => (
            <motion.div
              key={p.name}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-50px' }}
              transition={{ duration: 0.7, delay: (i % 2) * 0.12 }}
              data-testid={`player-card-${i}`}
              className="rounded-2xl border border-neutral-800 hover:border-gray-500 bg-black overflow-hidden transition-colors duration-300 grid grid-cols-1 sm:grid-cols-[2fr_3fr]"
            >
              <div className="relative aspect-[4/3] sm:aspect-auto sm:min-h-[280px]">
                <img src={p.photo} alt={p.name} className="absolute inset-0 w-full h-full object-cover object-top" />
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-black/40" />
              </div>

              <div className="p-5 sm:p-7 flex flex-col">
                <h3 className="font-display text-white text-2xl uppercase leading-tight">{p.name}</h3>
                <p className="text-gray-400 text-sm font-medium mt-1">{p.team}</p>

                {p.badge && (
                  <span className="inline-block w-fit bg-gray-300 text-black text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider mt-3">
                    {p.badge}
                  </span>
                )}

                <span className="inline-block w-fit border border-neutral-700 text-gray-300 text-xs font-bold tracking-wide uppercase px-3 py-1.5 rounded-lg mt-3">
                  {p.period}
                </span>

                <div className="flex items-center gap-2 flex-wrap mt-4">
                  <span className="text-gray-300 font-bold text-lg">{p.mainStat.label}</span>
                  <span className="font-display text-gray-400 text-2xl">{p.mainStat.before}</span>
                  <ArrowRight className="w-5 h-5 text-gray-500" />
                  <span className="font-display text-white text-3xl">{p.mainStat.after}</span>
                </div>

                {p.supporting.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-4">
                    {p.supporting.map((s) => (
                      <div key={s.label} className="border border-neutral-800 rounded-lg px-3 py-2 text-center">
                        <div className="text-gray-500 text-xs font-bold uppercase tracking-wide">{s.label}</div>
                        <div className="text-sm mt-0.5">
                          <span className="text-gray-400">{s.before}</span>
                          <span className="text-gray-600"> → </span>
                          <span className="text-white font-bold">{s.after}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <p className="text-gray-400 text-sm leading-relaxed italic mt-auto pt-4">{p.caption}</p>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.15 }}
          data-testid="credibility-line"
          className="rounded-2xl border border-neutral-800 bg-black p-6 sm:p-7 mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-5"
        >
          <ShieldCheck className="w-9 h-9 text-gray-300 shrink-0" strokeWidth={1.5} />
          <div className="flex-1">
            <p className="text-white font-bold">All stats are from official public sources.</p>
            <p className="text-gray-400 text-sm mt-1">
              Sources include WisSports, Bound/GoBound, and Nike EYBL. Comparisons are season-to-season:
              2024–25 Season vs 2025–26 Season.
            </p>
            <p className="text-gray-500 text-sm mt-1">
              <span className="text-gray-300 font-semibold">Exception:</span> Deuce McDuffie compares his
              2025–26 Nicolet HS Season vs 2026 Nike EYBL.
            </p>
          </div>
          <div className="flex items-center gap-5 text-gray-500 font-black text-sm tracking-widest uppercase shrink-0">
            <span>WisSports</span>
            <span>Bound</span>
            <span>EYBL</span>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.25 }}
          className="text-center mt-12"
        >
          <p className="text-white font-bold tracking-[0.3em] uppercase">Results Parents Can See</p>
          <p className="text-gray-500 text-sm mt-1">Real athletes. Real work. Real progress.</p>
        </motion.div>
      </div>
    </section>
  );
};
